import React, { useState, useEffect, useRef } from 'react';
import { useShop, useAuth } from '../App';
import PhotoEditorModal from './PhotoEditorModal';
import { toJpegOrPng } from '../utils/imageUpload';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const JEWELRY_TYPES = ['Ring', 'Earrings', 'Necklace', 'Bracelet', 'Watch', 'Other'];
const TICKET_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'on-hold', label: 'On Hold' }
];

// Default due date: 2 weeks from today
const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return date.toISOString().split('T')[0];
};

export default function RepairModal({ isOpen, onClose, repair, customer, onSuccess, onDelete, onGoToTransaction }) {
  const { shops, currentShop } = useShop();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showTransactionMenu, setShowTransactionMenu] = useState(false);
  const [detaching, setDetaching] = useState(false);
  const [detachedRepairId, setDetachedRepairId] = useState(null);
  const [shopId, setShopId] = useState('');
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    description: '',
    instructions: '',
    price: '',
    taxable: true,
    taxIncluded: false,
    jewelry_type: '',
    ticket_status: 'new',
    due_type: 'date',
    due_date: getDefaultDueDate()
  });
  const [photos, setPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState('description');
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setSearchQuery('');
      setSearchResults([]);
      setShowSearchResults(false);

      if (repair) {
        // Editing existing repair
        setSelectedCustomer({
          id: repair.customer_id,
          first_name: repair.first_name || '',
          last_name: repair.last_name || ''
        });
        setFormData({
          customer_id: repair.customer_id,
          title: repair.title || '',
          description: repair.description || '',
          instructions: repair.instructions || '',
          price: repair.price || '',
          taxable: repair.taxable !== undefined ? repair.taxable : true,
          taxIncluded: repair.taxIncluded || false,
          jewelry_type: repair.jewelry_type || '',
          ticket_status: repair.ticket_status || 'new',
          due_type: repair.due_type || 'date',
          due_date: repair.due_date ? (typeof repair.due_date === 'string' ? repair.due_date.split('T')[0] : repair.due_date) : getDefaultDueDate()
        });
        setPhotos(repair.photos || []);
        setShopId(repair.shop_id || '');
      } else if (customer) {
        // Pre-filled customer from context
        setSelectedCustomer(customer);
        setFormData(prev => ({
          ...prev,
          customer_id: customer.id,
          due_date: getDefaultDueDate()
        }));
        setPhotos([]);
        setShopId(currentShop?.shop_id || '');
      } else {
        setSelectedCustomer(null);
        setShopId(currentShop?.shop_id || '');
        setFormData({
          customer_id: '',
          title: '',
          description: '',
          instructions: '',
          price: '',
          taxable: true,
          taxIncluded: false,
          jewelry_type: '',
          ticket_status: 'new',
          due_type: 'date',
          due_date: getDefaultDueDate()
        });
        setPhotos([]);
      }
    }
  }, [isOpen, repair, customer]);

  // Listen for repair detached events from TransactionModal
  useEffect(() => {
    const handleRepairDetached = (event) => {
      if (repair && event.detail.repairId === repair.id) {
        setDetachedRepairId(repair.id);
      }
    };
    window.addEventListener('repairDetached', handleRepairDetached);
    return () => window.removeEventListener('repairDetached', handleRepairDetached);
  }, [repair]);

  // Derive effective transaction_id: null if detached locally
  const effectiveTransactionId = detachedRepairId === repair?.id ? null : repair?.transaction_id;

  // Search customers
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/customers/search/autocomplete?q=${encodeURIComponent(searchQuery)}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.customers || []);
          setShowSearchResults(true);
        }
      } catch (err) {
        console.error('Error searching customers:', err);
      }
    };
    const timer = setTimeout(fetchCustomers, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleCustomerSelect = (cust) => {
    setSelectedCustomer(cust);
    setFormData(prev => ({ ...prev, customer_id: cust.id }));
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handlePhotoUpload = async (input) => {
    // Support both event objects (from file input) and raw File objects (from PhotoEditorModal)
    const rawFile = input?.target?.files?.[0] || input;
    if (!rawFile) return null;

    setUploading(true);
    try {
      // Phones often hand over .webp; convert to JPEG so only jpg/png are uploaded
      const file = await toJpegOrPng(rawFile);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await fetch(`${API_URL}/uploads?folder=repairs`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      if (response.ok) {
        const data = await response.json();
        // When called from file input, update photos directly
        // When called from PhotoEditorModal, return the result
        if (input?.target) {
          setPhotos([...photos, { url: data.url, filename: data.originalname }]);
        }
        return data;
      }
      return null;
    } catch (err) {
      setError('Failed to upload photo');
      return null;
    } finally {
      setUploading(false);
      if (input?.target) input.target.value = '';
    }
  };

  const handlePhotoSave = (updatedPhotos) => {
    setPhotos(updatedPhotos);
    setShowPhotoEditor(false);
  };

  const handlePhotoEditorClose = () => {
    setShowPhotoEditor(false);
  };

  const handleRemovePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }
    if (!formData.title) {
      setError('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customer_id: formData.customer_id,
        title: formData.title,
        description: formData.description || null,
        instructions: formData.instructions || null,
        price: parseFloat(formData.price) || 0,
        taxable: formData.taxable,
        tax_included: formData.taxIncluded || false,
        jewelry_type: formData.jewelry_type || null,
        ticket_status: formData.ticket_status,
        due_type: formData.due_type,
        due_date: formData.due_date || null,
        photos: photos,
        shop_id: shopId || null
      };

      let res;
      if (repair) {
        res = await fetch(`${API_URL}/repairs/${repair.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API_URL}/repairs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Operation failed');
      }

      const data = await res.json();
      onSuccess(data.repair);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-gray-100 text-gray-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'on-hold': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleDetachRepair = async () => {
    setShowTransactionMenu(false);
    setDetaching(true);
    try {
      const res = await fetch(`${API_URL}/repairs/${repair.id}/detach`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to detach repair');
      }
      // Refresh the repair data by calling onSuccess to trigger parent refetch
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setDetaching(false);
    }
  };

  const handleGoToTransaction = () => {
    setShowTransactionMenu(false);
    if (onGoToTransaction) {
      onGoToTransaction(effectiveTransactionId);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Shop ID Field */}
              {isAdmin && repair ? (
                <select
                  value={shopId}
                  onChange={(e) => setShopId(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">No Shop</option>
                  {shops.map((shop) => (
                    <option key={shop.id} value={shop.shop_id}>{shop.shop_id}</option>
                  ))}
                </select>
              ) : (
                shopId && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {shopId}
                  </span>
                )
              )}
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {repair ? 'Edit Repair' : 'New Repair'}
            </h2>
            <div className="flex items-center gap-3">
              {/* Transaction Tag */}
              {effectiveTransactionId && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTransactionMenu(!showTransactionMenu)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    title={`Transaction #${effectiveTransactionId}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Transaction #{effectiveTransactionId}
                  </button>
                  {showTransactionMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowTransactionMenu(false)} />
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20 py-1">
                        <button
                          type="button"
                          onClick={handleGoToTransaction}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Go to Transaction
                        </button>
                        <button
                          type="button"
                          onClick={handleDetachRepair}
                          disabled={detaching}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          {detaching ? 'Detaching...' : 'Detach from Transaction'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              {repair && (
                <button
                  type="button"
                  onClick={() => window.open(`${API_URL}/repairs/${repair.id}/pdf?shop=ejr`, '_blank')}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                  title="Print Repair Ticket PDF"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </button>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            {/* Customer Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer <span className="text-red-500">*</span>
              </label>
              {selectedCustomer ? (
                <div className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <span className="text-sm">{selectedCustomer.first_name} {selectedCustomer.last_name}</span>
                  {!effectiveTransactionId && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setFormData(prev => ({ ...prev, customer_id: '' }));
                        searchInputRef.current?.focus();
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    autoComplete="off"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => showSearchResults && setShowSearchResults(true)}
                    onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                    placeholder="Search customer by name or phone..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {searchResults.map(cust => (
                        <button
                          key={cust.id}
                          type="button"
                          onClick={() => handleCustomerSelect(cust)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50"
                        >
                          {cust.first_name} {cust.last_name}
                          {cust.mobile_phone && <span className="text-gray-400 ml-2">({cust.mobile_phone})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="off"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g. Ring Resize, Chain Repair"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Jewelry Type and Ticket Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jewelry Type</label>
                <select
                  value={formData.jewelry_type}
                  onChange={(e) => setFormData({ ...formData, jewelry_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select type...</option>
                  {JEWELRY_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.ticket_status}
                  onChange={(e) => setFormData({ ...formData, ticket_status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {TICKET_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Price and Due Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                <input
                  type="number"
                  autoComplete="off"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  autoComplete="off"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Price and Taxable */}
            <div className="grid grid-cols-2 gap-4">

              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.taxable}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, taxable: !formData.taxable });
                    }}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-sm font-medium text-gray-700">Taxable</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.taxIncluded || false}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, taxIncluded: !formData.taxIncluded });
                    }}
                    className="w-4 h-4 ml-10 text-purple-600 rounded focus:ring-purple-500 cursor-pointer"
                  />
                  <span className="text-sm font-medium text-gray-700">Tax Included</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Type</label>
                <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="due_type"
                        value="date"
                        checked={formData.due_type === 'date'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, due_type: 'date' });
                        }}
                        className="border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">By Date</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="due_type"
                        value="will-call"
                        checked={formData.due_type === 'will-call'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, due_type: 'will-call' });
                        }}
                        className="border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Will Call</span>
                    </label>
                  </div>
              </div>
            </div>

            {/* Tabbed Section: Description, Instructions, Photos */}
            <div>
              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setActiveTab('description')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'description'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Description
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('instructions')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'instructions'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Instructions
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('photos')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                    activeTab === 'photos'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Photos
                  {photos.length > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                      {photos.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Tab Content */}
              <div className="py-4">
                {activeTab === 'description' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      autoComplete="off"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      placeholder="Describe the repair needed..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                )}
                {activeTab === 'instructions' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                    <textarea
                      autoComplete="off"
                      value={formData.instructions}
                      onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                      rows={4}
                      placeholder="Special instructions for the repair..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                )}
                {activeTab === 'photos' && (
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        {uploading ? 'Uploading...' : '+ Upload Photo'}
                      </button>
                      <span className="text-xs text-gray-400">{photos.length} photo(s)</span>
                    </div>
                    {photos.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {photos.map((photo, index) => (
                          <div key={index} className="relative w-20 h-20">
                            <img
                              src={photo.url}
                              alt={photo.filename}
                              className="w-full h-full object-cover rounded-md border border-gray-200 cursor-pointer"
                              onClick={() => setShowPhotoEditor(true)}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemovePhoto(index)}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0 flex justify-between items-center gap-3">
          <div className="flex gap-2">
            {repair && onDelete && !effectiveTransactionId ? (
              <button
                type="button"
                onClick={() => { onDelete(); onClose(); }}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50"
              >
                Delete Repair
              </button>
            ) : <div />}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : repair ? 'Update Repair' : 'Create Repair'}
            </button>
          </div>
        </div>
      </div>

      {/* Photo Editor Modal */}
      <PhotoEditorModal
        isOpen={showPhotoEditor}
        onClose={handlePhotoEditorClose}
        photos={photos}
        onSave={handlePhotoSave}
        onUpload={handlePhotoUpload}
      />
    </div>
  );
}
